import React from 'react';
import PropTypes from 'prop-types';
import { useTheme } from '@mui/material/styles';
import { Card, Skeleton } from '@mui/material';
import CardContent from '@mui/material/CardContent';
import Typography from '@mui/material/Typography';
import Stack from '@mui/material/Stack';
import { LineChart } from '@mui/x-charts/LineChart';


function AreaGradient({ color, id }) {
    return (
        <defs>
            <linearGradient id={id} x1="50%" y1="0%" x2="50%" y2="100%">
                <stop offset="0%" stopColor={color} stopOpacity={0.5} />
                <stop offset="100%" stopColor={color} stopOpacity={0} />
            </linearGradient>
        </defs>
    );
}

AreaGradient.propTypes = {
    color: PropTypes.string.isRequired,
    id: PropTypes.string.isRequired,
};

export default function EvGrowth({ chartData, loading }) {
    const theme = useTheme();
    const evGrowthData = chartData || [];


    const colorPalette = [
        theme.palette.primary.light,
        theme.palette.primary.main,
        theme.palette.primary.dark,
    ];

    return (
        <Card variant="outlined" sx={{ width: '100%', boxShadow: 2 }}>
            <CardContent>
                <Typography component="h2" variant="h6" gutterBottom fontWeight="medium">
                    Cumulative EV Growth Over Time
                </Typography>
                <Stack sx={{ justifyContent: 'space-between', mb: 2 }}>
                    <Typography variant="subtitle2" sx={{ color: 'text.secondary' }}>
                        Tracking the Growing Electric Vehicle Revolution
                    </Typography>
                </Stack>
                {evGrowthData?.length > 0 ? (
                    <LineChart
                        colors={colorPalette}
                        xAxis={[
                            {
                                id: 'organic',
                                scaleType: 'band',
                                data: evGrowthData.map(d => d.year),
                                valueFormatter: (value) => value.toString(),
                                tickLabelStyle: {
                                    angle: 0,
                                    textAnchor: 'middle',
                                    fontSize: 12
                                }
                            },
                        ]}
                        series={[
                            {
                                data: evGrowthData.map(d => d.count),
                                label: 'Total EVs',
                                curve: 'monotoneX',
                                color: theme.palette.primary.main,
                                connectNulls: true,
                                valueFormatter: (value) => `${value.toLocaleString()} EVs`,
                                highlightScope: {
                                    highlighted: 'point',
                                    faded: 'global'
                                },
                                showMark: true,
                                lineWidth: 2,
                            },
                        ]}
                        height={350}
                        margin={{ left: 50, right: 20, top: 20, bottom: 30 }}
                        grid={{
                            horizontal: true,
                            vertical: false,
                            strokeDasharray: '3 3',
                            stroke: theme.palette.divider
                        }}
                        sx={{
                            '& .MuiAreaElement-series-organic': {
                                fill: `url('#organic')`,
                            },
                            '& .MuiChartsLegend-mark': {
                                rx: '50%',
                            },
                            '.MuiLineElement-root': {
                                strokeWidth: '2px',
                            },
                            '.MuiMarkElement-root': {
                                stroke: theme.palette.background.paper,
                                scale: '1.1',
                                fill: theme.palette.primary.main,
                            },
                        }}
                        slotProps={{
                            legend: {
                                hidden: true,
                            },
                        }}
                    >
                        <AreaGradient color={theme.palette.primary.main} id="organic" />
                    </LineChart>
                ) : (
                    <Skeleton variant="rectangular" height={350} sx={{ bgcolor: theme.palette.background.paper }} />
                )}
            </CardContent>
        </Card>
    );
}
